import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { getMemberInsights } from 'src/app/home/adapters'
import { mapMemberInsightItem } from 'src/app/home/pages/member-insights/domain/use-cases/map-member-insight-item'
import type { IInsightEntity } from 'src/app/home/pages/member-insights/types'
import { memberInsightsKeys } from 'src/app/home/queries/member-insights-keys'

export interface IMemberInsightsQueryResult {
  items: IInsightEntity[]
  total: number
  page: number
  pageSize: number
}

export interface IUseMemberInsightsQueryParams {
  page: number
  pageSize: number
  search: string
  category: string
  enabled: boolean
  /** When true, the global query error toast is suppressed (caller handles UX). */
  silentErrorToast?: boolean
}

export const useMemberInsightsQuery = (
  params: IUseMemberInsightsQueryParams,
): UseQueryResult<IMemberInsightsQueryResult, Error> => {
  return useQuery({
    queryKey: memberInsightsKeys.list({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      category: params.category,
    }),
    ...(params.silentErrorToast ? { meta: { silent: true } } : {}),
    queryFn: async (): Promise<IMemberInsightsQueryResult> => {
      const response = await getMemberInsights({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search || undefined,
        category: params.category || undefined,
      })
      return {
        items: response.data.map(mapMemberInsightItem),
        total: response.total,
        page: response.page,
        pageSize: response.page_size,
      }
    },
    enabled: params.enabled,
  })
}
